import React, { createContext, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountRegionalSettings } from '@/hooks/useAccountRegionalSettings'
import { setCurrencyDisplay, type CurrencyDisplay } from '@/lib/currency'
import { resetAccountTimezone } from '@/lib/accountTimezone'
import { resolveBootstrapLanguage, type UiLanguage } from '@/lib/uiLanguage'

interface AccountRegionalContextType {
  currencyDisplay: CurrencyDisplay
  uiLanguage: UiLanguage
  timezone: string
  loading: boolean
  saving: boolean
  error: string | null
  updateCurrencyDisplay: (value: CurrencyDisplay) => Promise<boolean>
  updateUiLanguage: (value: UiLanguage) => Promise<boolean>
  updateTimezone: (value: string) => Promise<boolean>
  refetch: () => Promise<void>
}

const AccountRegionalContext = createContext<AccountRegionalContextType | undefined>(
  undefined
)

export const useAccountRegional = () => {
  const context = useContext(AccountRegionalContext)
  if (context === undefined) {
    throw new Error('useAccountRegional must be used within an AccountRegionalProvider')
  }
  return context
}

interface AccountRegionalProviderProps {
  children: React.ReactNode
}

/**
 * Loads account regional prefs and keeps currency formatters, i18n language and the
 * timezone used by date helpers in sync.
 * Safe outside auth: falls back to BRL / pt-BR / America/Sao_Paulo when there is no session.
 */
export const AccountRegionalProvider: React.FC<AccountRegionalProviderProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth()
  const { i18n } = useTranslation()
  const settings = useAccountRegionalSettings()

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrencyDisplay('BRL')
      resetAccountTimezone()
      // Currency and timezone belong to the account, but the language does not reset to pt-BR
      // on logout: the login screen should stay in the language this user actually works in.
      const publicLanguage = resolveBootstrapLanguage()
      if (i18n.language !== publicLanguage) {
        void i18n.changeLanguage(publicLanguage)
      }
    }
  }, [isAuthenticated, i18n])

  return (
    <AccountRegionalContext.Provider value={settings}>
      {children}
    </AccountRegionalContext.Provider>
  )
}
